import React, { useEffect, useMemo, useState } from "react";
import General from "./General";
import CommissionPayouts from "./CommissionPayouts";
import LandingPages from './LandingPages';
import Affiliates from './Affiliates';
import AffiliateTracking from './AffiliateTracking';
import Integrations from './Integrations';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

import { FaCog, FaMoneyBillWave, FaGlobe, FaPaintBrush, FaChartLine, FaPlug} from "react-icons/fa";
import { campaignService } from "../../services/companyAuthService";
import LimitReachedOverlay from "../../../modules/shared/LimitReachedOverlay";
import LoadingSpinner from "../../../components/LoadingSpinner";

const CreateCampaign = () => {
  const [activeStep, setActiveStep] = useState(1);
  const navigate = useNavigate();
  const [campaignLimits, setCampaignLimits] = useState(null);
  const [limitsLoading, setLimitsLoading] = useState(true);

  const [campaignData, setCampaignData] = useState({
    general: null,
    commission: null,
    landingPage: null,
    affiliates: null,
    tracking: null,
    integrations: null
  });

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        setLimitsLoading(true);
        const res = await campaignService.getCampaignLimits();
        if (res?.success && res?.data) setCampaignLimits(res.data);
      } catch {
        // If limits API fails, do not block creation (backend still enforces).
        setCampaignLimits(null);
      } finally {
        setLimitsLoading(false);
      }
    };
    fetchLimits();
  }, []);

  // Get dynamic step names based on campaign type
  const getStepName = (stepId) => {
    const campaignType = campaignData?.general?.campaignType;
    const isAdvertisingCampaign = campaignType === 'Advertising';
    
    switch (stepId) {
      case 1:
        return "General";
      case 2:
        return "Commission & payouts";
      case 3:
        return isAdvertisingCampaign ? "Brand Info" : "Landing pages";
      case 4:
        return "Affiliates";
      case 5:
        return "Affiliate tracking";
      case 6:
        return "Integrations";
      default:
        return "";
    }
  };

  const steps = useMemo(
    () => [
      { id: 1, name: "General", icon: <FaCog /> },
      { id: 2, name: "Commission & payouts", icon: <FaMoneyBillWave /> },
      { id: 3, name: getStepName(3), icon: <FaGlobe /> },
      { id: 4, name: "Affiliates", icon: <FaPaintBrush /> },
      { id: 5, name: "Affiliate tracking", icon: <FaChartLine /> },
      { id: 6, name: "Integrations", icon: <FaPlug /> },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignData?.general?.campaignType]
  );

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!campaignData.general) {
          return false;
        }
        const { name, brandId, campaignType, startDate, endDate } = campaignData.general;
        if (!name || !brandId || !campaignType || !startDate || !endDate) {
          return false;
        }
        return true;

      case 2:
        if (!campaignData.commission) {
          return false;
        }
        const { model, currency } = campaignData.commission;
        if (!model || !currency?.code) {
          return false;
        }
        
        // Additional validation for advertising and hybrid campaigns
        const isAdvertisingOrHybrid = campaignData.general?.campaignType === 'Advertising' || 
                                     campaignData.general?.campaignType === 'Hybrid';
        const isHybridCampaign = campaignData.general?.campaignType === 'Hybrid';
        
        if (isAdvertisingOrHybrid && model === 'fixed') {
          if (campaignData.commission.cpe === undefined || campaignData.commission.cpe === null || 
              campaignData.commission.cpm === undefined || campaignData.commission.cpm === null) {
            return false;
          }
        }
        
        // Validate CPE and CPM for hybrid campaigns with percentage model
        if (isHybridCampaign && model === 'percentage') {
          if (campaignData.commission.cpe === undefined || campaignData.commission.cpe === null || 
              campaignData.commission.cpm === undefined || campaignData.commission.cpm === null) {
            return false;
          }
        }
        
        // Validate amount or percentage based on model
        if (model === 'fixed' && !campaignData.commission.amount) {
          return false;
        }
        if (model === 'percentage' && !campaignData.commission.percentage) {
          return false;
        }
        
        return true;

      case 3:
        if (!campaignData.landingPage) {
          return false;
        }
        return true;

      case 4:
        // Add validation for Affiliates step if needed
        return true;

      case 5:
        // Add validation for Affiliate Tracking step if needed
        return true;

      case 6:
        // Add validation for Integrations step if needed
        return true;

      default:
        return true;
    }
  };

  const handleNext = (stepData) => {
    console.log('handleNext called with stepData:', stepData, 'activeStep:', activeStep);
    
    // First update the campaign data
    let updatedData = { ...campaignData };
    switch (activeStep) {
      case 1:
        updatedData.general = stepData;
        break;
      case 2:
        updatedData.commission = stepData;
        break;
      case 3:
        updatedData.landingPage = stepData;
        break;
      case 4:
        updatedData.affiliates = stepData;
        break;
      case 5:
        updatedData.tracking = stepData;
        break;
      case 6:
        updatedData.integrations = stepData;
        break;
      default:
        break;
    }

    console.log('Updated campaign data:', updatedData);

    // Update the state with new data
    setCampaignData(updatedData);
    
    // Validate the current step with the updated data (not the old state)
    const isValid = (() => {
      switch (activeStep) {
        case 1:
                  const { name, brandId, campaignType, startDate, endDate } = stepData;
        const step1Valid = name && brandId && campaignType && startDate && endDate;
        console.log('Step 1 validation:', { name, brandId, campaignType, startDate, endDate, valid: step1Valid });
          return step1Valid;

        case 2:
          const { model, currency } = stepData;
          if (!model || !currency?.code) {
            console.log('Step 2 validation failed: missing model or currency');
            return false;
          }
          
          const isAdvertisingOrHybrid = updatedData.general?.campaignType === 'Advertising' || 
                                       updatedData.general?.campaignType === 'Hybrid';
          const isHybridCampaign = updatedData.general?.campaignType === 'Hybrid';
          
          if (isAdvertisingOrHybrid && model === 'fixed') {
            if (stepData.cpe === undefined || stepData.cpe === null || stepData.cpm === undefined || stepData.cpm === null) {
              console.log('Step 2 validation failed: missing CPE or CPM for advertising/hybrid');
              return false;
            }
          }
          
          // Validate CPE and CPM for hybrid campaigns with percentage model
          if (isHybridCampaign && model === 'percentage') {
            if (stepData.cpe === undefined || stepData.cpe === null || stepData.cpm === undefined || stepData.cpm === null) {
              console.log('Step 2 validation failed: missing CPE or CPM for hybrid percentage model');
              return false;
            }
          }
          
          if (model === 'fixed' && !stepData.amount) {
            console.log('Step 2 validation failed: missing amount for fixed model');
            return false;
          }
          if (model === 'percentage' && !stepData.percentage) {
            console.log('Step 2 validation failed: missing percentage for percentage model');
            return false;
          }
          
          console.log('Step 2 validation passed');
          return true;

        case 3:
          const step3Valid = !!stepData && stepData.trim() !== '';
          console.log('Step 3 validation:', { stepData, valid: step3Valid });
          return step3Valid;

        default:
          return true;
      }
    })();

    console.log('Step validation result:', isValid);
    if (isValid) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleStepClick = (stepId) => {
    // Only allow clicking on completed steps or the next available step
    const currentStep = activeStep;
    if (stepId <= currentStep) {
      // Validate the step we're trying to go to
      if (validateStep(stepId)) {
        setActiveStep(stepId);
      } else {
        const stepName = getStepName(stepId);
        toast.error(`Please complete all required fields in ${stepName} section`);
      }
    } else {
      toast.error("Please complete the current step first");
    }
  };

  const renderContent = () => {
    switch (activeStep) {
      case 1:
        return <General onNext={handleNext} data={campaignData.general} />;
      case 2:
        return <CommissionPayouts 
          onNext={handleNext} 
          onPrevious={handlePrevious}
          data={campaignData.commission} 
        />;
      case 3:
        return <LandingPages 
          onNext={(landingPageUrl) => {
            // Update campaign data with landing page URL
            const updatedData = { ...campaignData, landingPage: landingPageUrl };
            setCampaignData(updatedData);
          }}
          onPrevious={handlePrevious}
          campaignData={campaignData}
        />;
      case 4:
        return <Affiliates 
          onNext={handleNext}
          onPrevious={handlePrevious}
          data={campaignData.affiliates}
        />;
      case 5:
        return <AffiliateTracking 
          onNext={handleNext}
          onPrevious={handlePrevious}
          data={campaignData.tracking}
        />;
      case 6:
        return <Integrations 
          onPrevious={handlePrevious}
          data={campaignData.integrations}
        />;
      default:
        return <General />;
    }
  };

  const limitReached = !limitsLoading && campaignLimits?.canCreate === false;
  const limitMessage =
    campaignLimits?.campaignLimitType === "per_month"
      ? "You've reached your monthly campaign creation limit."
      : "You've reached your limit of active campaigns.";

  if (limitsLoading) {
    return (
      <div className="flex flex-col lg:flex-row min-h-full bg-gray-100">
        <div className="flex-1 p-2 md:p-4 lg:p-6 lg:mt-0 min-h-full flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row min-h-full bg-gray-100">
        <div className="flex-1 p-2 md:p-4 lg:p-6 lg:mt-0 relative min-h-full">
          <LimitReachedOverlay
            open={limitReached}
            title="Campaign Limit Reached"
            message={`${limitMessage} Upgrade your plan to create more campaigns and unlock advanced features!`}
            primaryLabel="Upgrade Plan"
            onPrimary={() => navigate("/pricing")}
            secondaryLabel="Cancel"
            onSecondary={() => navigate("/company/dashboard/campaigns")}
          />
          <div className="bg-white shadow-md rounded-lg p-4 lg:p-6 mb-10 lg:mb-0">
            <h2 className="text-2xl font-bold mb-4">Make New Campaign</h2>
            <nav className="text-sm text-gray-500 mb-4">
              <span>Create new campaign &gt; </span>
              <span className="text-gray-700">{getStepName(activeStep)}</span>
            </nav>

            <div className="flex flex-col lg:flex-row">
              {/* Steps Section */}
              <div className="w-full lg:w-1/4 mb-4 lg:mb-0 lg:pr-4">
                <div className="space-y-4">
                  {steps.map((step) => (
                    <div
                      key={step.id}
                      onClick={() => handleStepClick(step.id)}
                      className={`flex items-center space-x-2 cursor-pointer ${
                        activeStep === step.id ? "text-orange-600" : 
                        step.id < activeStep ? "text-orange-600" : "text-gray-400"
                      }`}
                    >
                      <span
                        className={`w-6 h-6 flex items-center justify-center ${
                          activeStep === step.id
                            ? "bg-orange-600 text-white"
                            : step.id < activeStep
                            ? "bg-orange-600 text-white"
                            : "border"
                        } rounded-full`}
                      >
                        {step.icon}
                      </span>
                      <span>{getStepName(step.id)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="w-full md:w-3/4">
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateCampaign;

